import { useState, useMemo, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Input,
    Select,
    SelectItem,
    Selection,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import useRequest from "@/lib/request/useRequest";
import { createFileRoute } from "@tanstack/react-router";
import { InferType } from "yup";
import SelectInput from "@/components/inputs/SelectInput";
import TextInput from "@/components/inputs/TextInput";
import { FertilizerType, ProductType } from "@/types/products";
import {
    faPlus,
    faEdit,
    faTrash,
    faTint,
    faLeaf,
    faHeart,
    faStar,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";
import { Resource } from "@/types/user";
import TableV2 from "@/components/table/TableV2";
export const Route = createFileRoute("/dashboard/account-settings/products")({
    component: Products,
});

interface Product {
    id: number;
    name: string;
    fertilizer_type: FertilizerType;
    density: number | null;
    lbs_N_per_gallon: number | null;
    percent_N: number | null;
    created_at?: string;
    updated_at?: string;
    is_active?: boolean;
    organization?: number;
    is_favorite?: boolean;
    core_product?: boolean;
    product_type?: ProductType;
}

const schema = yup.object().shape({
    name: yup.string().max(100).required("Name is required"),
    fertilizer_type: yup
        .string()
        .oneOf([
            String(FertilizerType.Dry),
            String(FertilizerType.Liquid),
            String(FertilizerType.PlantGrowthRegulator),
            String(FertilizerType.HarvestAid),
        ])
        .required("Type is required"),
    density: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
    lbs_N_per_gallon: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
    percent_N: yup
        .number()
        .required("Percent N is required")
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
    product_type: yup.string().oneOf(Object.values(ProductType)).required("Product type is required"),
});

type FormData = InferType<typeof schema>;

function Products() {
    const { t } = useTranslation();
    const { userIsSentinel, isAtLeastSentinel } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(
        null,
    );
    const [typeFilter, setTypeFilter] = useState<Selection>(
        new Set(["all"]),
    );
    const [coreOnly, setCoreOnly] = useState(false);
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sorting, setSorting] = useState<SortingState>([
        { id: "name", desc: false },
    ]);
    const { useGetRequest, useMutateRequest } = useRequest();
    const {
        data: products,
        isLoading,
        error,
        refetch,
    } = useGetRequest<Product[]>({
        url: "/fertilizers/",
    });

    const transformedProducts = useMemo(() => {
        return products?.map((product) => ({
            ...product,
            fertilizer_type: Number(product.fertilizer_type) as FertilizerType,
        }));
    }, [products]);

    const mutateProduct = useMutateRequest({
        successMessage: t("products.product_updated_successfully", {
            ns: "accountSettings",
        }),
        errorMessage: t("products.failed_to_update_product", {
            ns: "accountSettings",
        }),
    });

    const deleteProduct = useMutateRequest({
        successMessage: t("products.product_deleted_successfully", {
            ns: "accountSettings",
        }),
        errorMessage: t("products.failed_to_delete_product", {
            ns: "accountSettings",
        }),
    });

    const favoriteProduct = useMutateRequest({
        method: "put",
    });
    const form = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            fertilizer_type: String(FertilizerType.Liquid),
            density: null,
            lbs_N_per_gallon: null,
            percent_N: 0,
            product_type: ProductType.Fertilizer,
        },
    });

    const handleEdit = useCallback(
        (product: Product) => {
            setSelectedProduct(product);
            form.reset({
                ...product,
                fertilizer_type: String(product.fertilizer_type),
                percent_N: product.percent_N ?? 0,
                product_type: product.product_type ?? ProductType.Fertilizer,
            });
            setIsModalOpen(true);
        },
        [form],
    );

    const COLUMNS = useMemo<ColumnDef<Product>[]>(
        () => [
            {
                accessorKey: "name",
                header: t("products.name", { ns: "accountSettings" }),
                size: 200,
            },
            {
                accessorKey: "fertilizer_type",
                header: t("products.type", { ns: "accountSettings" }),
                size: 160,
                cell: ({ row }) => {
                    const type = row.original.fertilizer_type;
                    if (type === FertilizerType.Liquid) {
                        return (
                            <>
                                <FontAwesomeIcon
                                    icon={faTint}
                                    className="text-blue-500 mr-1"
                                />
                                {t("products.liquid", {
                                    ns: "accountSettings",
                                })}
                            </>
                        );
                    }
                    if (type === FertilizerType.PlantGrowthRegulator) {
                        return (
                            <>
                                <FontAwesomeIcon
                                    icon={faLeaf}
                                    className="text-green-600 mr-1"
                                />
                                {t("products.plant_growth_regulator", {
                                    ns: "accountSettings",
                                })}
                            </>
                        );
                    }
                    if (type === FertilizerType.HarvestAid) {
                        return (
                            <>
                                <FontAwesomeIcon
                                    icon={faLeaf}
                                    className="text-yellow-600 mr-1"
                                />
                                {t("products.harvest_aid", {
                                    ns: "accountSettings",
                                })}
                            </>
                        );
                    }
                    return (
                        <>
                            <FontAwesomeIcon
                                icon={faLeaf}
                                className="text-green-600 mr-1"
                            />
                            {t("products.dry", { ns: "accountSettings" })}
                        </>
                    );
                },
            },
            {
                accessorKey: "density",
                header: t("products.density", { ns: "accountSettings" }),
                size: 120,
                cell: ({ getValue }) => getValue() ?? "-",
            },
            {
                accessorKey: "lbs_N_per_gallon",
                header: t("products.lbs_n_gal", { ns: "accountSettings" }),
                size: 140,
                cell: ({ getValue }) => getValue() ?? "-",
            },
            {
                accessorKey: "percent_N",
                header: t("products.percent_n", { ns: "accountSettings" }),
                size: 120,
                cell: ({ getValue }) => getValue() ?? "-",
            },
            {
                accessorKey: "product_type",
                header: t("products.product_type", { ns: "accountSettings" }),
                size: 130,
                cell: ({ getValue }) => {
                    const value = getValue() as ProductType | undefined;
                    if (!value) return "-";
                    return t(`products.${value}`, { ns: "accountSettings" });
                },
            },
            {
                id: "actions",
                header: t("products.actions", { ns: "accountSettings" }),
                size: 120,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex gap-2">
                        {userIsSentinel && (
                            <Button
                                size="sm"
                                variant="flat"
                                onPress={() => handleEdit(row.original)}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faEdit}
                                        className="h-4 w-4"
                                    />
                                }
                            >
                                {t("products.edit", { ns: "accountSettings" })}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant={row.original.is_favorite ? "solid" : "bordered"}
                            isIconOnly
                            onPress={() => {
                                favoriteProduct.mutate({
                                    url: `/fertilizers/${row.original.id}/favorite/`,
                                    method: "put",
                                    data: {
                                        is_favorite: !row.original.is_favorite,
                                    },
                                }, {
                                    onSuccess: () => {
                                        refetch();
                                    },
                                });
                            }}
                            color="danger"
                        >
                            <FontAwesomeIcon icon={faHeart} />
                        </Button>
                        {userIsSentinel && (
                            <Button
                                size="sm"
                                variant={row.original.core_product ? "solid" : "bordered"}
                                isIconOnly
                                onPress={() => {
                                    mutateProduct.mutate({
                                        url: `/fertilizers/${row.original.id}/`,
                                        method: "patch",
                                        data: {
                                            core_product: !row.original.core_product,
                                        },
                                    }, {
                                        onSuccess: () => {
                                            refetch();
                                        },
                                    });
                                }}
                                color="warning"
                            >
                                <FontAwesomeIcon icon={faStar} />
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        [t, userIsSentinel, handleEdit, favoriteProduct, refetch, mutateProduct],
    );

    const filteredProducts = useMemo(() => {
        const selectedKey = Array.from(typeFilter)[0] as string | undefined;
        return (
            transformedProducts?.filter((product: Product) => {
                const matchesSearch = product.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());
                const matchesType =
                    !selectedKey ||
                    selectedKey === "all" ||
                    product.fertilizer_type === Number(selectedKey);
                const matchesFavorite =
                    !favoritesOnly || Boolean(product.is_favorite);
                const matchesCore =
                    !coreOnly || Boolean(product.core_product);
                return matchesSearch && matchesType && matchesFavorite && matchesCore;
            }) ?? []
        );
    }, [transformedProducts, searchQuery, typeFilter, favoritesOnly, coreOnly]);

    const handleAdd = () => {
        setSelectedProduct(null);
        form.reset({
            name: "",
            fertilizer_type: String(FertilizerType.Liquid),
            density: null,
            lbs_N_per_gallon: null,
            percent_N: 0,
            product_type: ProductType.Fertilizer,
        });
        setIsModalOpen(true);
    };

    const onSubmit = async (data: FormData) => {
        const isEdit = !!selectedProduct;
        const url = isEdit
            ? `/fertilizers/${selectedProduct.id}/`
            : "/fertilizers/";
        const method = isEdit ? "put" : "post";
        const body = isEdit
            ? {
                ...data,
                id: selectedProduct.id,
                fertilizer_type: Number(data.fertilizer_type),
            }
            : { ...data, fertilizer_type: Number(data.fertilizer_type) };

        await mutateProduct.mutateAsync({
            url,
            method,
            data: body,
        });

        form.reset();
        setSelectedProduct(null);
        setIsModalOpen(false);
        await refetch();
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;
        await deleteProduct.mutateAsync({
            url: `/fertilizers/${selectedProduct.id}/`,
            method: "delete",
        });
        setIsModalOpen(false);
        await refetch();
    };

    const handleModalClose = () => {
        form.reset();
        setSelectedProduct(null);
        setIsModalOpen(false);
    };

    if (error)
        return (
            <div>
                {t("products.error_loading_products", {
                    ns: "accountSettings",
                })}
            </div>
        );

    return (
        <div className="h-full flex flex-col gap-2">
            <div className="flex w-full flex-wrap items-center gap-2 justify-between">
                <div className="min-w-0 flex-1 basis-48">
                    <Input
                        placeholder={t("products.search_products", {
                            ns: "accountSettings",
                        })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="max-w-md"
                    />
                </div>
                <div className="flex shrink-0 flex-row flex-wrap items-center gap-2">
                    <Select
                        aria-label={t("products.filter_by_type", {
                            ns: "accountSettings",
                        })}
                        className="w-56 shrink-0"
                        disallowEmptySelection
                        selectedKeys={typeFilter}
                        selectionMode="single"
                        onSelectionChange={setTypeFilter}
                        placeholder={t("products.all_types", {
                            ns: "accountSettings",
                        })}
                        showScrollIndicators={false}
                    >
                        <SelectItem key="all">
                            {t("products.all_types", { ns: "accountSettings" })}
                        </SelectItem>
                        <SelectItem key={String(FertilizerType.Liquid)}>
                            {t("products.liquid", { ns: "accountSettings" })}
                        </SelectItem>
                        <SelectItem key={String(FertilizerType.Dry)}>
                            {t("products.dry", { ns: "accountSettings" })}
                        </SelectItem>
                        <SelectItem
                            key={String(FertilizerType.PlantGrowthRegulator)}
                        >
                            {t("products.plant_growth_regulator", {
                                ns: "accountSettings",
                            })}
                        </SelectItem>
                        <SelectItem key={String(FertilizerType.HarvestAid)}>
                            {t("products.harvest_aid", {
                                ns: "accountSettings",
                            })}
                        </SelectItem>
                    </Select>
                    <Button
                        className="shrink-0"
                        variant={favoritesOnly ? "solid" : "bordered"}
                        color="danger"
                        startContent={
                            <FontAwesomeIcon
                                icon={faHeart}
                                className="h-4 w-4"
                            />
                        }
                        onPress={() =>
                            setFavoritesOnly((prev) => !prev)
                        }
                    >
                        {t("products.favorites_filter", {
                            ns: "accountSettings",
                        })}
                    </Button>
                    <Button
                        className="shrink-0"
                        variant={coreOnly ? "solid" : "bordered"}
                        color="warning"
                        startContent={
                            <FontAwesomeIcon
                                icon={faStar}
                                className="h-4 w-4"
                            />
                        }
                        onPress={() =>
                            setCoreOnly((prev) => !prev)
                        }
                    >
                        {t("products.core_filter", {
                            ns: "accountSettings",
                        })}
                    </Button>
                    {isAtLeastSentinel(Resource.CREATE_PRODUCTS) && (
                        <Button
                            color="primary"
                            className="shrink-0"
                            onPress={handleAdd}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faPlus}
                                />
                            }
                        >
                            {t("products.add_new_product", {
                                ns: "accountSettings",
                            })}
                        </Button>
                    )}
                </div>
            </div>

            <div className="h-full">
                <TableV2
                    data={filteredProducts}
                    columns={COLUMNS}
                    sorting={sorting}
                    setSorting={setSorting}
                    isLoading={isLoading}
                    estimateSize={48}
                    fillWidth
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={handleModalClose} size="lg">
                <ModalContent>
                    <ModalHeader>
                        {selectedProduct
                            ? t("products.edit_product", {
                                ns: "accountSettings",
                            })
                            : t("products.add_new_product", {
                                ns: "accountSettings",
                            })}
                    </ModalHeader>
                    <ModalBody>
                        <FormProvider {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-4 p-4"
                            >
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.name", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput name="name" />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.type", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <SelectInput
                                        name="fertilizer_type"
                                        options={[
                                            {
                                                id: String(
                                                    FertilizerType.Liquid,
                                                ),
                                                description: t(
                                                    "products.liquid",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: String(FertilizerType.Dry),
                                                description: t("products.dry", {
                                                    ns: "accountSettings",
                                                }),
                                            },
                                            {
                                                id: String(
                                                    FertilizerType.PlantGrowthRegulator,
                                                ),
                                                description: t(
                                                    "products.plant_growth_regulator",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: String(
                                                    FertilizerType.HarvestAid,
                                                ),
                                                description: t(
                                                    "products.harvest_aid",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.density", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="density"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.lbs_n_gal", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="lbs_N_per_gallon"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.percent_n", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="percent_N"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.product_type", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <SelectInput
                                        name="product_type"
                                        options={[
                                            {
                                                id: ProductType.Fertilizer,
                                                description: t(
                                                    "products.fertilizer",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: ProductType.Manure,
                                                description: t(
                                                    "products.manure",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: ProductType.Biological,
                                                description: t(
                                                    "products.biological",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: ProductType.Biostimulant,
                                                description: t(
                                                    "products.biostimulant",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                        ]}
                                    />
                                </div>

                                <div className="flex justify-end gap-2">
                                    {selectedProduct && (
                                        <Button
                                            color="danger"
                                            variant="flat"
                                            onPress={handleDelete}
                                            isLoading={mutateProduct.isPending}
                                            isDisabled={mutateProduct.isPending}
                                            className="mr-auto"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="h-4 w-4"
                                                />
                                            }
                                        >
                                            {t("products.delete", {
                                                ns: "accountSettings",
                                            })}
                                        </Button>
                                    )}
                                    <Button
                                        variant="flat"
                                        onPress={handleModalClose}
                                        disabled={mutateProduct.isPending}
                                    >
                                        {t("products.cancel", {
                                            ns: "accountSettings",
                                        })}
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        isLoading={mutateProduct.isPending}
                                        isDisabled={mutateProduct.isPending}
                                    >
                                        {t("products.save", {
                                            ns: "accountSettings",
                                        })}
                                    </Button>
                                </div>
                            </form>
                        </FormProvider>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
}
